import resourceTranslations from '@/translations/resource.fr.yml';
import { useMemo } from 'react';
import { Search } from '@/types/algolia';
import isGranted from '@/components/app/users/isGranted';
import { useSelector } from 'react-redux';
import { ReduxRootState } from '@/types/store/ReduxRootState';
import { Publication } from '@/types/core/Publication';
import { Resource } from '@/types/core/Resource';

export function useSubHeaderCategoriesFromAlgoliaFacetSearch(
  algoliaCategories: Search
) {
  const availableCategories = useGetAvailableResourcesCategories(false);

  return useMemo(() => {
    const facets =
      algoliaCategories?.searchResult?.facets?.mainCategories ?? {};
    return availableCategories.map(({ value, label }) => ({
      value,
      label,
      count: facets[value] ?? 0,
    }));
  }, [algoliaCategories, availableCategories]);
}

export default function useGetAvailableResourcesCategories(
  filterByPermissions: boolean = true
): {
  value: string;
  label: string;
  subchoices: { value: string; label: unknown }[];
}[] {
  const currentUser = useSelector(
    (state: ReduxRootState) => state.authentication.user
  );
  return useMemo(() => {
    const allowAll =
      isGranted(currentUser, 'ROLE_WORLD_ORGANISER') || !filterByPermissions;
    return Object.entries(resourceTranslations)
      .filter(
        entry =>
          entry[0].startsWith('category.') &&
          (allowAll || entry[0] !== 'category.if_resources')
      )
      .map(entry => {
        const entryKey = entry[0].slice(9);
        return {
          value: entry[0],
          label: entry[1] as string,
          subchoices: resourceTranslations.category[entryKey]
            ? Object.entries(resourceTranslations.category[entryKey]).map(
                subentry => {
                  return {
                    value: `${entry[0]}.${subentry[0]}`,
                    label: subentry[1],
                  };
                }
              )
            : [],
        };
      });
  }, [resourceTranslations, currentUser]);
}

export function useTranslatedResourceThemes(
  publication: Publication
): string[] {
  return useMemo<string[]>(() => {
    if (!publication.categories) return [];

    let themes = [];
    let categories = [];
    for (let publicationCategory of publication.categories) {
      let items = publicationCategory.split('.');
      if (items.length === 2) {
        themes.push(resourceTranslations['category.' + items[1]]);
      }
      if (items.length === 3) {
        categories.push(resourceTranslations.category[items[1]][items[2]]);
      }
    }
    return themes;
  }, [publication]);
}

export function useTranslatedCategories(publication?: Publication) {
  return useMemo<string[]>(() => {
    if (!publication?.categories) return [];

    return publication.categories.flatMap(category => {
      const [_, categoryType, subCategory] = category.split('.');
      return subCategory
        ? resourceTranslations.category[categoryType][subCategory]
        : [];
    });
  }, [publication]);
}

export function useTranslatedCECRLevels(publication: Publication): string[] {
  return useMemo<string[]>(() => {
    if (!(publication as Resource).requiredCECRLevels) return [];
    const resource = publication as Resource;

    // Sort the levels by their translated name
    return resource.requiredCECRLevels
      .map(level => resourceTranslations.cecr_level[level])
      .sort((a, b) => a.localeCompare(b));
  }, [publication]);
}
